import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { firestoreService } from '@/services/firestoreService';
import { functionsService } from '@/services/functionsService';
import { decodeHtmlEntities } from '@/utils/textUtils';

const PLACEHOLDER_TEXT = '[Transcript unavailable - previously stored filing metadata.]';

function isPlaceholderTranscript(text: string | undefined): boolean {
  if (!text || text.length < 50) return true;
  const t = text.trim();
  return t === PLACEHOLDER_TEXT || (t.includes('Transcript unavailable') && t.length < 200);
}

interface Transcript {
  id: string;
  companyId: string;
  companyName: string;
  ticker: string;
  quarter: string;
  date: any;
  text: string;
}

interface Filing {
  id: string;
  companyId: string;
  companyName: string;
  ticker: string;
  filingType: string;
  date: any;
  sourceUrl?: string;
}

export const TranscriptManagement: React.FC = () => {
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [filings, setFilings] = useState<Filing[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingFilings, setLoadingFilings] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [populating, setPopulating] = useState(false);
  const [replacingPlaceholders, setReplacingPlaceholders] = useState(false);
  const [populateLimit, setPopulateLimit] = useState(100);
  const [activeTab, setActiveTab] = useState<'transcripts' | 'filings'>('transcripts');
  const [viewingTranscript, setViewingTranscript] = useState<Transcript | null>(null);

  useEffect(() => {
    loadTranscripts();
    loadFilings();
  }, []);

  // Auto-refresh transcript list every 60s so new data from background job appears
  useEffect(() => {
    if (activeTab !== 'transcripts') return;
    const interval = setInterval(() => {
      loadTranscripts();
      loadFilings();
    }, 60000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const loadTranscripts = async () => {
    try {
      setLoading(true);
      const data = await firestoreService.getDocuments<Transcript>('transcripts', [
        firestoreService.orderBy('date', 'desc'),
        firestoreService.limit(500),
      ]);
      console.log('Loaded transcripts:', data.length);
      setTranscripts(data);
    } catch (error: any) {
      console.error('Failed to load transcripts:', error);
      // Show user-friendly error message
      if (error?.message?.includes('index') || error?.message?.includes('Firestore index')) {
        const indexUrl = (error as any)?.indexUrl || error?.message?.match(/https:\/\/console\.firebase\.google\.com[^\s\)]+/)?.[0];
        if (indexUrl) {
          const shouldOpen = confirm(`Missing Firestore index for transcripts.\n\nClick OK to open the index creation page, or Cancel to continue.\n\n${indexUrl}`);
          if (shouldOpen) {
            window.open(indexUrl, '_blank');
          }
        } else {
          alert('Missing Firestore index for transcripts. Please check Firebase Console → Firestore → Indexes to create the required index.');
        }
      } else if (error?.message) {
        console.error('Error details:', error.message);
        // Don't show alert for permission errors - they're handled elsewhere
        if (!error?.message?.includes('Permission denied')) {
          console.warn('Failed to load transcripts:', error.message);
        }
      }
      setTranscripts([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadFilings = async () => {
    try {
      setLoadingFilings(true);
      const data = await firestoreService.getDocuments<Filing>('filings', [
        firestoreService.orderBy('date', 'desc'),
        firestoreService.limit(500),
      ]);
      console.log('Loaded filings:', data.length);
      setFilings(data);
    } catch (error: any) {
      console.error('Failed to load filings:', error);
      // Show user-friendly error message
      if (error?.message?.includes('index') || error?.message?.includes('Firestore index')) {
        const indexUrl = (error as any)?.indexUrl || error?.message?.match(/https:\/\/console\.firebase\.google\.com[^\s\)]+/)?.[0];
        if (indexUrl) {
          const shouldOpen = confirm(`Missing Firestore index for filings.\n\nClick OK to open the index creation page, or Cancel to continue.\n\n${indexUrl}`);
          if (shouldOpen) {
            window.open(indexUrl, '_blank');
          }
        } else {
          alert('Missing Firestore index for filings. Please check Firebase Console → Firestore → Indexes to create the required index.');
        }
      } else if (error?.message) {
        console.error('Error details:', error.message);
        // Don't show alert for permission errors - they're handled elsewhere
        if (!error?.message?.includes('Permission denied')) {
          console.warn('Failed to load filings:', error.message);
        }
      }
      setFilings([]); // Set empty array on error
    } finally {
      setLoadingFilings(false);
    }
  };

  const handlePopulateTranscripts = async () => {
    const message = `This will fetch earnings transcripts for up to ${populateLimit} companies. This may take several minutes.\n\n⚠️ NOTE: FMP STARTER plan does not include earnings call transcripts. The function will automatically use SEC EDGAR fallback (limited coverage ~10-20% success rate).\n\nFor best results, use "Upload Transcript" for specific transcripts you need.\n\nContinue?`;
    if (!confirm(message)) {
      return;
    }

    try {
      setPopulating(true);
      console.log('Calling populateEarningsTranscripts with limit:', populateLimit);
      const result = await functionsService.populateEarningsTranscripts(populateLimit);
      console.log('Populate result:', result);
      
      if (result.success) {
        // Show detailed message including fetched count and companies processed
        let message = '';
        if (result.fetched !== undefined) {
          message = `Processed ${result.companiesProcessed || 0} companies.\n\n`;
          message += `Fetched: ${result.fetched} transcripts\n`;
          message += `Created: ${result.created || 0} new transcripts\n`;
          message += `Skipped: ${result.skipped || 0} existing transcripts\n`;
          message += `Errors: ${result.errors || 0}\n`;
          if (result.companiesWithNoTranscripts !== undefined) {
            message += `\nCompanies with no transcripts: ${result.companiesWithNoTranscripts}`;
          }
          if (result.fetched === 0 && result.message) {
            message += `\n\n${result.message}`;
          }
        } else if (result.created !== undefined) {
          message = `Success! Created ${result.created} transcripts, skipped ${result.skipped || 0} existing, ${result.errors || 0} errors.`;
        } else {
          message = result.message || 'Transcripts are being fetched. This may take a few minutes.';
        }
        alert(message);
        // Reload transcripts and filings after a delay to see new ones
        setTimeout(() => {
          loadTranscripts();
          loadFilings();
        }, 5000);
      } else {
        alert('Failed to populate transcripts. Please check the console for details.');
      }
    } catch (error: any) {
      console.error('Failed to populate transcripts:', error);
      let errorMessage = 'Unknown error';
      
      if (error?.code === 'deadline-exceeded' || error?.message?.includes('deadline')) {
        errorMessage = 'Request timed out. The function is still processing in the background. Try reducing the limit (e.g., 20 instead of 100) or check Firebase logs for progress.';
      } else if (error?.code === 'internal') {
        errorMessage = 'Internal server error. The function may have timed out processing 100 companies. Try a smaller limit (e.g., 20).';
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.code) {
        errorMessage = `Error code: ${error.code}`;
      }
      
      alert(`Failed to populate transcripts: ${errorMessage}\n\nCheck browser console and Firebase logs for details.`);
    } finally {
      setPopulating(false);
    }
  };

  const handleReplacePlaceholders = async () => {
    const limit = Math.min(100, Math.max(1, populateLimit));
    if (!confirm(`Replace up to ${limit} placeholder transcript records with real content (SEC / API Ninjas / FMP)? This may take a few minutes.`)) {
      return;
    }
    try {
      setReplacingPlaceholders(true);
      const result = await functionsService.replacePlaceholderTranscripts(limit);
      if (result?.success) {
        const msg = [
          result.replaced != null && `Replaced: ${result.replaced}`,
          result.notFound != null && `Not found: ${result.notFound}`,
          result.errors != null && result.errors > 0 && `Errors: ${result.errors}`,
          result.processed != null && `Processed: ${result.processed}`,
        ].filter(Boolean).join(' · ');
        alert(msg || result.message || 'Done.');
        loadTranscripts();
      } else {
        alert((result as any)?.message || 'Replace placeholders failed.');
      }
    } catch (error: any) {
      console.error('Replace placeholders error:', error);
      alert(`Failed: ${error?.message || 'Unknown error'}`);
    } finally {
      setReplacingPlaceholders(false);
    }
  };

  const handlePopulateAllData = async () => {
    if (!confirm(`This will fetch transcripts, filings, and other data for up to ${populateLimit} companies. This may take 10-15 minutes. Continue?`)) {
      return;
    }

    try {
      setPopulating(true);
      console.log('Calling populateAllCompanyData with limit:', populateLimit, 'backgroundMode: true');
      const result = await functionsService.populateAllCompanyData(populateLimit, true, true); // Enable background mode
      console.log('Populate all data result:', result);
      
      if (result.success) {
        if (result.backgroundMode) {
          // First batch already ran; refresh list so user sees the update
          await loadTranscripts();
          await loadFilings();
          alert(`✅ Background job started!\n\nData will be fetched every 1 minute until complete. Refresh the list in a few minutes to see new data.\n\nJob ID: ${result.jobId}`);
          const pollInterval = setInterval(() => {
            loadTranscripts();
            loadFilings();
          }, 30000);
          setTimeout(() => clearInterval(pollInterval), 5 * 60 * 1000);
        } else {
          const stats = result.results;
          const message = stats 
            ? `Success! Created: ${stats.transcripts.created} transcripts, ${stats.filings.created} filings, ${stats.news.created} news items, ${stats.pressReleases.created} press releases. Skipped: ${stats.transcripts.skipped + stats.filings.skipped + stats.news.skipped + stats.pressReleases.skipped} existing items. Errors: ${(stats.transcripts.errors || 0) + (stats.filings.errors || 0) + (stats.news.errors || 0) + (stats.pressReleases.errors || 0)}.`
            : result.message || 'Data population started. This may take several minutes.';
          alert(message);
        }
        // Refresh again after a short delay in case writes are still propagating
        setTimeout(() => {
          loadTranscripts();
          loadFilings();
        }, 3000);
      } else {
        alert('Failed to populate data. Please check the console for details.');
      }
    } catch (error: any) {
      console.error('Failed to populate data:', error);
      const errorMessage = error?.message || error?.code || 'Unknown error';
      alert(`Failed to populate data: ${errorMessage}\n\nCheck browser console for details.`);
    } finally {
      setPopulating(false);
    }
  };

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    companyName: '',
    ticker: '',
    quarter: '',
    year: '',
    date: '',
    text: '',
  });

  const handleUploadTranscript = async () => {
    if (!uploadData.companyName || !uploadData.ticker || !uploadData.quarter || !uploadData.year || !uploadData.text) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      // Find company by ticker
      const companies = await firestoreService.getDocuments<{ id: string; name?: string }>('companies', [
        firestoreService.where('ticker', '==', uploadData.ticker.toUpperCase()),
        firestoreService.limit(1),
      ]);

      if (companies.length === 0) {
        alert(`Company with ticker ${uploadData.ticker} not found. Please add the company first.`);
        return;
      }

      const companyId = companies[0].id;
      const transcriptDate = uploadData.date ? new Date(uploadData.date) : new Date();

      // Create transcript document
      await firestoreService.createDocument('transcripts', {
        companyId,
        companyName: uploadData.companyName,
        ticker: uploadData.ticker.toUpperCase(),
        quarter: uploadData.quarter,
        year: parseInt(uploadData.year),
        date: transcriptDate,
        text: uploadData.text,
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      alert('Transcript uploaded successfully!');
      setShowUploadModal(false);
      setUploadData({
        companyName: '',
        ticker: '',
        quarter: '',
        year: '',
        date: '',
        text: '',
      });
      loadTranscripts();
    } catch (error: any) {
      console.error('Failed to upload transcript:', error);
      alert(`Failed to upload transcript: ${error.message}`);
    }
  };

  const handleTriggerAnalysis = async (transcriptId: string) => {
    try {
      await functionsService.analyzeTranscript(transcriptId);
      alert('Analysis triggered successfully');
    } catch (error: any) {
      alert(`Failed to trigger analysis: ${error.message}`);
    }
  };

  const filteredTranscripts = transcripts.filter(
    (t) =>
      t.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.ticker?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.quarter?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFilings = filings.filter(
    (f) =>
      f.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.ticker?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.filingType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
        <Input
          type="text"
          placeholder={`Search ${activeTab}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
        
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="body-text text-sm text-gray-600">Limit:</label>
            <Input
              type="number"
              min="1"
              max="100"
              value={populateLimit}
              onChange={(e) => setPopulateLimit(parseInt(e.target.value) || 20)}
              className="w-20"
            />
          </div>
          <Button
            variant="outline"
            onClick={handlePopulateTranscripts}
            disabled={populating}
          >
            {populating ? 'Fetching...' : 'Fetch Transcripts'}
          </Button>
          <Button
            variant="outline"
            onClick={handleReplacePlaceholders}
            disabled={populating || replacingPlaceholders}
            title="Replace placeholder transcript records with real content from SEC / API Ninjas / FMP"
          >
            {replacingPlaceholders ? 'Replacing...' : 'Replace placeholders'}
          </Button>
          <Button
            variant="primary"
            onClick={handlePopulateAllData}
            disabled={populating}
          >
            {populating ? 'Fetching All Data...' : 'Fetch All Data'}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              loadTranscripts();
              loadFilings();
            }}
            disabled={loading || loadingFilings}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowUploadModal(true)}
          >
            Upload Transcript
          </Button>
        </div>
      </div>

      {showUploadModal && (
        <Card className="mb-4">
          <div className="p-6">
            <h3 className="sub-header text-xl mb-4">Upload Manual Transcript</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block body-text text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <Input
                    value={uploadData.companyName}
                    onChange={(e) => setUploadData({ ...uploadData, companyName: e.target.value })}
                    placeholder="Apple Inc."
                  />
                </div>
                <div>
                  <label className="block body-text text-sm font-medium text-gray-700 mb-1">
                    Ticker *
                  </label>
                  <Input
                    value={uploadData.ticker}
                    onChange={(e) => setUploadData({ ...uploadData, ticker: e.target.value })}
                    placeholder="AAPL"
                  />
                </div>
                <div>
                  <label className="block body-text text-sm font-medium text-gray-700 mb-1">
                    Quarter *
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="4"
                    value={uploadData.quarter}
                    onChange={(e) => setUploadData({ ...uploadData, quarter: e.target.value })}
                    placeholder="1"
                  />
                </div>
                <div>
                  <label className="block body-text text-sm font-medium text-gray-700 mb-1">
                    Year *
                  </label>
                  <Input
                    type="number"
                    value={uploadData.year}
                    onChange={(e) => setUploadData({ ...uploadData, year: e.target.value })}
                    placeholder="2024"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block body-text text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <Input
                    type="date"
                    value={uploadData.date}
                    onChange={(e) => setUploadData({ ...uploadData, date: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block body-text text-sm font-medium text-gray-700 mb-1">
                  Transcript Text *
                </label>
                <textarea
                  value={uploadData.text}
                  onChange={(e) => setUploadData({ ...uploadData, text: e.target.value })}
                  className="w-full h-64 p-3 border border-gray-300 rounded-lg font-mono text-sm"
                  placeholder="Paste the full earnings call transcript here..."
                />
                <p className="body-text text-xs text-gray-500 mt-1">
                  {uploadData.text.length} characters
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  onClick={handleUploadTranscript}
                  disabled={!uploadData.companyName || !uploadData.ticker || !uploadData.quarter || !uploadData.year || !uploadData.text}
                >
                  Upload
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadData({
                      companyName: '',
                      ticker: '',
                      quarter: '',
                      year: '',
                      date: '',
                      text: '',
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {populating && (
        <Card className="mb-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3 p-4">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blazerex-green"></div>
            <p className="body-text text-sm text-blue-800">
              Fetching transcripts from Financial Modeling Prep API. This may take several minutes...
            </p>
          </div>
        </Card>
      )}

      {/* Tabs + Refresh */}
      <div className="mb-4 flex items-center gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('transcripts')}
          className={`px-4 py-2 body-text font-medium ${
            activeTab === 'transcripts'
              ? 'border-b-2 border-blazerex-green text-blazerex-green'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Transcripts ({transcripts.length})
        </button>
        <button
          onClick={() => setActiveTab('filings')}
          className={`px-4 py-2 body-text font-medium ${
            activeTab === 'filings'
              ? 'border-b-2 border-blazerex-green text-blazerex-green'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Filings ({filings.length})
        </button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { loadTranscripts(); loadFilings(); }}
          className="ml-auto"
        >
          Refresh list
        </Button>
      </div>

      {/* Transcripts Tab */}
      {activeTab === 'transcripts' && (
        <>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
            </div>
          ) : (
            <Card>
              {filteredTranscripts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="body-text text-gray-600 mb-2">No transcripts found</p>
                  <p className="body-text text-sm text-gray-500">
                    Click "Fetch Transcripts" to populate transcripts from the API
                  </p>
                </div>
              ) : (
                <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 sub-header">Company</th>
                        <th className="text-left py-3 px-4 sub-header">Quarter</th>
                        <th className="text-left py-3 px-4 sub-header">Date</th>
                        <th className="text-left py-3 px-4 sub-header">Transcript</th>
                        <th className="text-left py-3 px-4 sub-header">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTranscripts.map((transcript) => {
                        const placeholder = isPlaceholderTranscript(transcript.text);
                        const wordCount = transcript.text ? transcript.text.trim().split(/\s+/).filter(Boolean).length : 0;
                        return (
                          <tr key={transcript.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <div>
                                <p className="body-text font-medium">{transcript.companyName}</p>
                                <p className="body-text text-sm text-gray-500 font-mono">{transcript.ticker}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4 body-text">{transcript.quarter}</td>
                            <td className="py-3 px-4 body-text text-sm">
                              {transcript.date ? (
                                new Date(transcript.date?.toDate?.() || transcript.date).toLocaleDateString()
                              ) : (
                                <span className="text-gray-400">N/A</span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2 flex-wrap">
                                {placeholder ? (
                                  <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">Placeholder</span>
                                ) : (
                                  <span className="body-text text-sm text-gray-600">{wordCount} words</span>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setViewingTranscript(transcript)}
                                  className="text-blazerex-green"
                                >
                                  View
                                </Button>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleTriggerAnalysis(transcript.id)}
                                disabled={placeholder}
                                title={placeholder ? 'Transcript has no real content' : undefined}
                              >
                                Analyze
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {viewingTranscript && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setViewingTranscript(null)}>
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="sub-header text-lg">
                          {viewingTranscript.companyName} ({viewingTranscript.ticker}) — {viewingTranscript.quarter}
                        </h3>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(viewingTranscript.text || ''); }}>Copy</Button>
                          <Button variant="outline" size="sm" onClick={() => setViewingTranscript(null)}>Close</Button>
                        </div>
                      </div>
                      <div className="p-6 overflow-y-auto flex-1 bg-gray-50">
                        <div className="max-w-3xl mx-auto">
                          <p className="body-text text-sm text-gray-500 mb-3">
                            {isPlaceholderTranscript(viewingTranscript.text)
                              ? 'This record has no transcript content (placeholder).'
                              : 'Transcript content below. If this looks like a financial statement instead of an earnings call, the source data may be incorrect.'}
                          </p>
                          <div className="body-text text-gray-800 whitespace-pre-wrap leading-relaxed rounded-lg bg-white p-4 border border-gray-200">
                            {decodeHtmlEntities(viewingTranscript.text || '') || '(No transcript text)'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                </>
              )}
            </Card>
          )}
        </>
      )}

      {/* Filings Tab */}
      {activeTab === 'filings' && (
        <>
          {loadingFilings ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
            </div>
          ) : (
            <Card>
              {filteredFilings.length === 0 ? (
                <div className="text-center py-12">
                  <p className="body-text text-gray-600 mb-2">No filings found</p>
                  <p className="body-text text-sm text-gray-500">
                    Click "Fetch All Data" to populate filings from the API
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 sub-header">Company</th>
                        <th className="text-left py-3 px-4 sub-header">Filing Type</th>
                        <th className="text-left py-3 px-4 sub-header">Date</th>
                        <th className="text-left py-3 px-4 sub-header">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFilings.map((filing) => (
                        <tr key={filing.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="body-text font-medium">{filing.companyName}</p>
                              <p className="body-text text-sm text-gray-500 font-mono">{filing.ticker}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 body-text">
                            <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                              {filing.filingType}
                            </span>
                          </td>
                          <td className="py-3 px-4 body-text text-sm">
                            {filing.date ? (
                              new Date(filing.date?.toDate?.() || filing.date).toLocaleDateString()
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {filing.sourceUrl && (
                              <a
                                href={filing.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blazerex-green hover:underline body-text text-sm"
                              >
                                View Source
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
};

